import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, debounceTime, EMPTY, Subject, switchMap } from 'rxjs';
import { SupportService } from 'src/app/services/core/support/support.service';
import { sweetAlert } from 'src/app/utils/swal';
import { SUPPORT_CONTACT_TYPES, SUPPORT_PAGE_SIZES } from 'src/app/utils/support.utils';
@UntilDestroy()

// a type dropdown and a searchable dropdown of that type's records
@Component({
  selector: 'app-support-contact-picker',
  templateUrl: './support-contact-picker.component.html',
  styleUrls: ['./support-contact-picker.component.css']
})
export class SupportContactPickerComponent implements OnInit, OnChanges {
  @Input() types: any[] = SUPPORT_CONTACT_TYPES;
  @Input() typeLabel = 'Type';
  // broker staff: only this tenant's records
  @Input() scope: any;
  @Input() required = false;
  @Input() invalid = false;
  @Input() stacked = false;
  @Output() selectType = new EventEmitter<any>();
  @Output() selectContact = new EventEmitter<any>();

  selectedType = '';
  selectedContact: any = null;
  contacts: any = [];
  isLoading = false;
  // the api does the filtering
  readonly searchSubject = new Subject<string>();
  private readonly contactsRequest = new Subject<string>();

  constructor(private supportService: SupportService) { }

  ngOnInit() {
    this.selectedType = this.types[0].value;
    this.listenSearch();
    this.listenContactsRequests();
    this.getContacts('');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['scope'] && !changes['scope'].firstChange) {
      this.onSelectType(this.types[0].value);
    }
  }

  get selectedTypeOption() {
    return this.types.find((type) => type.value === this.selectedType);
  }

  listenSearch() {
    this.searchSubject.pipe(debounceTime(500), untilDestroyed(this)).subscribe((search) => this.getContacts(search || ''));
  }

  // the api returns the first 10, the rest are found by searching
  getContacts(search: string) {
    // e.g. a general ticket isn't about any record
    if (this.selectedTypeOption?.noRecords) {
      return;
    }
    this.contactsRequest.next(search);
  }

  listenContactsRequests() {
    this.contactsRequest.pipe(
      switchMap((search) => {
        this.isLoading = true;
        const queryParams: any = { type: this.selectedType, search, limit: SUPPORT_PAGE_SIZES.references };
        if (this.scope) {
          queryParams.scopeType = this.scope.type;
          queryParams.scopeId = this.scope.id;
        }
        return this.supportService.searchReferences(queryParams).pipe(
          catchError((err) => {
            this.isLoading = false;
            sweetAlert('Error', err, 'error', 'OK');
            return EMPTY;
          })
        );
      }),
      untilDestroyed(this)
    ).subscribe((data: any) => {
      // the picked record stays in the list while searching
      const isListed = data.some((contact: any) => contact.id === this.selectedContact?.id);
      this.contacts = this.selectedContact && !isListed ? [this.selectedContact, ...data] : data;
      this.isLoading = false;
    });
  }

  onSelectType(type: string) {
    this.selectedType = type;
    this.contacts = [];
    this.onSelectContact(null);
    this.selectType.emit(this.selectedTypeOption);
    this.getContacts('');
  }

  onSelectContact(contact: any) {
    this.selectedContact = contact;
    this.selectContact.emit(contact);
  }
}
